import { Injectable, NgZone } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import {
  CallerPresenceListener,
  CallerPresenceState
} from "../../libraries/firebase/realtime/caller-presence-listener";
import { StateEmitter } from "../../libraries/emitter/state-emitter";

import { AuthService, AuthState } from "./auth.service";

@Injectable({ providedIn: "root" })
/** Service that mantains state of firebase callers table. Essentially a list of lurkers / potential callers */
export class CallersPresentService extends StateEmitter<Array<CallerPresenceState>> {
  private authState: AuthState | undefined;
  private callerPresenceListener: CallerPresenceListener | undefined;
  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(private userAuth: AuthService, private zone: NgZone) {
    super([]);
    this.init();
  }

  dispose(): void {
    this.publishStateComplete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stopCallerPresence();
  }

  protected publishRun(fn: () => any): any {
    return this.zone.run(() => {
      return fn();
    });
  }

  private get oid(): number {
    if (!this.authState || !this.authState.oid) {
      throw new Error("oid is undefined.");
    }
    return this.authState.oid;
  }

  private init(): void {
    Promise.resolve().then(() => {
      this.subscribeAuth();
    });
  }

  private signedIn(): void {
    this.startCallerPresence();
  }

  private signedOut(): void {
    this.stopCallerPresence();
  }

  private subscribeAuth(): void {
    this.userAuth.state.pipe(takeUntil(this.unsubscribe)).subscribe(state => {
      const didChange = AuthService.didAuthStatusChange(this.authState, state);
      this.authState = state;
      switch (didChange) {
        case "signedIn":
          return this.signedIn();
        case "signedOut":
          return this.signedOut();
        default:
          return;
      }
    });
  }

  private startCallerPresence(): void {
    this.callerPresenceListener = new CallerPresenceListener(this.oid);
    this.callerPresenceListener.state.subscribe(callers => {
      this.stateStore = callers;
      this.publishState();
    });

    this.callerPresenceListener.start();
  }

  private stopCallerPresence(): void {
    if (this.callerPresenceListener) {
      this.callerPresenceListener.dispose();
      this.callerPresenceListener = undefined;
    }
  }
}
